import { RefundBillIcon } from "@images/svgIcons/money/refundBillIcon"
import { word } from "@modules/core/utils/i18n"
import { formatCurrency } from "@modules/currency/currency"
import { TransactionDetailsEntity } from "@modules/finances/entities/transactionDetailsEntity"
import { useRefundTransaction } from "@modules/finances/hooks/useFinanceActions"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { HUButton } from "@modules/ui/components/huButton"
import { HUCardAlert } from "@modules/ui/components/huCardAlert"
import { HUText } from "@modules/ui/components/huText"
import Line from "@modules/ui/components/line"
import { HUDialog } from "@modules/ui/components/popup/huDialog"
import dayjs from "dayjs"
import { Info } from "lucide-react"
import { useCallback, useImperativeHandle, useState } from "react"
import { useTheme } from "styled-components"

export type TransactionRefundModalRef = {
  open: () => void
}

type Props = {
  transactionDetails: TransactionDetailsEntity
  ref: React.Ref<TransactionRefundModalRef>
}

export function TransactionRefundModal({ transactionDetails, ref }: Props) {
  const theme = useTheme()
  const [showModal, setShowModal] = useState(false)

  const language = useLanguage()
  const { refundTransaction, isPending: isPendingRefundTransaction } = useRefundTransaction()

  const linesLenght = transactionDetails.lines.length ?? 0

  const onHide = useCallback(() => {
    setShowModal(false)
  }, [])

  useImperativeHandle(ref, () => ({
    open: () => {
      setShowModal(true)
    },
  }))

  const handleRefund = async (): Promise<void> => {
    if (!transactionDetails.refundable) return
    refundTransaction(transactionDetails.transactionId).then(() => {
      setShowModal(false)
    })
  }

  return (
    <HUDialog visible={showModal} header={null} onHide={onHide} style={{ width: "480px" }}>
      <div className="flex flex-column gap-6">
        {/* HEADER */}
        <div className="flex flex-column flex-wrap pt-4 gap-2 justify-content-center align-content-center text-center">
          <div className="flex w-full justify-content-center align-content-center">
            <RefundBillIcon color={theme.mainWhite} />
          </div>
          <HUText fontStyle="TL" color={theme.blackAtheleWhiteOwner}>
            {word("box.transaction.refund.modal.alert.title")}
          </HUText>
        </div>

        {/* ALERTE */}
        <HUCardAlert
          title={word("box.transaction.refund.modal.alert.content")}
          icon={<Info size={35} className="mx-2" style={{ minWidth: 35 }} />}
          className="py-2 border-round-2xl align-items-center"
          titleFontStyle="BS"
        />

        <div className="flex flex-column gap-2">
          <HUText
            fontStyle="TS"
            className="flex justify-content-center align-content-center text-center"
            color={theme.blackAtheleWhiteOwner}
          >
            {word("box.transaction.refund.modal.header.content")}
          </HUText>
          {transactionDetails.lines.map((invoice, index) => (
            <div key={index} className="flex flex-column gap-2">
              <div className="flex gap-5 px-2">
                <div className="flex flex-grow-1 flex-column">
                  <HUText fontStyle="BM" className="flex" color={theme.blackAtheleWhiteOwner}>
                    {`${invoice.description}`}
                  </HUText>
                  {invoice.periodEnd && invoice.periodStart ? (
                    <HUText fontStyle="BS" className="flex" color={theme.neutralColor400}>
                      {`${dayjs(invoice.periodStart).format("L")} - ${dayjs(invoice.periodEnd).format("L")}`}
                    </HUText>
                  ) : null}
                </div>
                <HUText fontStyle="BM" className="flex" color={theme.blackAtheleWhiteOwner}>
                  {`${formatCurrency(transactionDetails.amount ?? 0, transactionDetails.currency, language)}`}
                </HUText>
              </div>

              {index < linesLenght - 1 ? (
                <Line color={theme.neutralColor300} strokeWidth={1} className="m-0 rounded-full" />
              ) : null}
            </div>
          ))}
        </div>

        {/* BUTTON */}
        <div className="mb-3 flex flex-row gap-2 align-content-center align-items-center justify-content-center">
          <HUButton
            type="Left_Icon"
            size="M"
            colorType="Quaternary"
            text={word("global.cancel")}
            onClick={(event) => {
              event.preventDefault()
              setShowModal(false)
            }}
            className="w-full"
          />
          <HUButton
            type="Right_Icon"
            size="M"
            colorType="Primary"
            text={word("box.transaction.refund.modal.alert.action")}
            onClick={handleRefund}
            disabled={isPendingRefundTransaction}
            className="w-full"
          />
        </div>
      </div>
    </HUDialog>
  )
}
